/**
 * Ticket System API Service
 * Handles all API calls for the ticket management system
 */

import apiClient from '../client'
import {
  TicketSummaryDto,
  TicketDetailsDto,
  TicketCommentDto,
  TicketStatisticsDto,
  CreateTicketRequest,
  AddCommentRequest,
  ResolveTicketRequest,
  UpdatePriorityRequest,
  EscalateTicketRequest,
  AssignTicketRequest,
  TicketStatus,
  TicketPriority,
  TicketType,
} from '../types/tickets'

const BASE_PATH = '/api/Tickets'

// User Endpoints

/**
 * Create a new ticket
 */
export async function createTicket(request: CreateTicketRequest): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(BASE_PATH, request)
  return response.data
}

/**
 * Get current user's tickets with pagination
 */
export async function getMyTickets(page = 1, pageSize = 20): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/my-tickets`, {
    params: { page, pageSize },
  })
  return response.data
}

/**
 * Get detailed information about a specific ticket
 */
export async function getTicketDetails(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.get<TicketDetailsDto>(`${BASE_PATH}/${ticketId}`)
  return response.data
}

/**
 * Add a comment to a ticket
 */
export async function addComment(ticketId: string, request: AddCommentRequest): Promise<TicketCommentDto> {
  const response = await apiClient.post<TicketCommentDto>(`${BASE_PATH}/${ticketId}/comments`, request)
  return response.data
}

/**
 * Get all comments for a ticket
 */
export async function getTicketComments(ticketId: string): Promise<TicketCommentDto[]> {
  const response = await apiClient.get<TicketCommentDto[]>(`${BASE_PATH}/${ticketId}/comments`)
  return response.data
}

/**
 * Close a ticket (creator only)
 */
export async function closeTicket(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/close`)
  return response.data
}

// Moderator Endpoints

/**
 * Get available tickets pool (pending tickets sorted by priority)
 * Requires: Moderator role
 */
export async function getTicketPool(page = 1, pageSize = 20): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/pool`, {
    params: { page, pageSize },
  })
  return response.data
}

/**
 * Get tickets claimed by current moderator
 * Requires: Moderator role
 */
export async function getMyClaimedTickets(page = 1, pageSize = 20): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/claimed`, {
    params: { page, pageSize },
  })
  return response.data
}

/**
 * Get tickets assigned to current moderator
 * Requires: Moderator role
 */
export async function getMyAssignedTickets(page = 1, pageSize = 20): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/assigned`, {
    params: { page, pageSize },
  })
  return response.data
}

/**
 * Claim a ticket from the pool
 * Requires: Moderator role
 */
export async function claimTicket(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/claim`)
  return response.data
}

/**
 * Unclaim a previously claimed ticket
 * Requires: Moderator role
 */
export async function unclaimTicket(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/unclaim`)
  return response.data
}

/**
 * Start progress on a ticket
 * Requires: Moderator role
 */
export async function startProgress(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/start-progress`)
  return response.data
}

/**
 * Mark ticket as awaiting user response
 * Requires: Moderator role
 */
export async function markAwaitingUser(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/await-user`)
  return response.data
}

/**
 * Resolve a ticket
 * Requires: Moderator role
 */
export async function resolveTicket(ticketId: string, request: ResolveTicketRequest): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/resolve`, request)
  return response.data
}

/**
 * Reopen a closed or resolved ticket
 * Requires: Moderator role or ticket creator
 */
export async function reopenTicket(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/reopen`)
  return response.data
}

// Admin Endpoints

/**
 * Update ticket priority
 * Requires: Admin role
 */
export async function updatePriority(ticketId: string, request: UpdatePriorityRequest): Promise<TicketDetailsDto> {
  const response = await apiClient.put<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/priority`, request)
  return response.data
}

/**
 * Escalate a ticket
 * Requires: Admin role
 */
export async function escalateTicket(ticketId: string, request: EscalateTicketRequest): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/escalate`, request)
  return response.data
}

/**
 * Assign ticket to a specific moderator
 * Requires: Admin role
 */
export async function assignTicket(ticketId: string, request: AssignTicketRequest): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/assign`, request)
  return response.data
}

// Filtering & Search Endpoints

/**
 * Get tickets by status
 * Requires: Moderator role
 */
export async function getTicketsByStatus(
  status: TicketStatus,
  page = 1,
  pageSize = 20
): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/status/${TicketStatus[status]}`, {
    params: { page, pageSize },
  })
  return response.data
}

/**
 * Get tickets by priority
 * Requires: Moderator role
 */
export async function getTicketsByPriority(
  priority: TicketPriority,
  page = 1,
  pageSize = 20
): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/priority/${TicketPriority[priority]}`, {
    params: { page, pageSize },
  })
  return response.data
}

/**
 * Get tickets by type
 * Requires: Moderator role
 */
export async function getTicketsByType(type: TicketType, page = 1, pageSize = 20): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/type/${TicketType[type]}`, {
    params: { page, pageSize },
  })
  return response.data
}

/**
 * Get escalated tickets
 * Requires: Admin role
 */
export async function getEscalatedTickets(page = 1, pageSize = 20): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/escalated`, {
    params: { page, pageSize },
  })
  return response.data
}

/**
 * Search tickets by keyword
 * Requires: Moderator role
 */
export async function searchTickets(searchTerm: string, page = 1, pageSize = 20): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/search`, {
    params: { searchTerm, page, pageSize },
  })
  return response.data
}

/**
 * Get ticket statistics
 * Requires: Admin role
 */
export async function getTicketStatistics(): Promise<TicketStatisticsDto> {
  const response = await apiClient.get<TicketStatisticsDto>(`${BASE_PATH}/statistics`)
  return response.data
}

/**
 * Check if current user has pending moderation tickets
 * Returns true if the user has any pending tickets they can moderate
 * Checks both system-level tickets (for Moderators/Admins) and group-level tickets
 */
export async function hasPendingModerationTickets(): Promise<boolean> {
  const response = await apiClient.get<boolean>(`${BASE_PATH}/has-pending`)
  return response.data
}
